import { type KeyboardEvent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty } from "@huub-web/utils";

import type { FieldConstraints } from "../types";
import type { FormInputType, DataFieldValue } from "../types";
import { InputConfiguration } from "../services/inputs/InputConfiguration";

export interface DynamicInputValidationState {
  validationProps: {
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    maxLength?: number;
    min?: number;
    max?: number;
    required?: boolean;
  };
  validate: (value: DataFieldValue) => DynamicInputValidationResult;
}

export interface DynamicInputValidationResult {
  success: boolean;
  errors?: string[];
  value?: DataFieldValue;
}

export type DynamicInputCustomValidationCallback = (
  value: DataFieldValue,
) => boolean;

interface DynamicInputValidationOptions {
  required?: boolean;
  /**
   * Dynamic constraints from backend json_schema_extra.validation.
   * Merged with static InputConfiguration rules.
   */
  constraints?: FieldConstraints;
}

/**
 * Builds a localized error message for a constraint violation.
 * Uses frontend i18n keys as primary source, backend message as fallback.
 */
function buildConstraintError({
  t,
  constraints,
  failedKey,
}: {
  t: (key: string, options?: Record<string, unknown>) => string;
  constraints: FieldConstraints;
  failedKey: "gt" | "gte" | "lt" | "lte";
}): string {
  // Always use the specific failed key — avoids ambiguity between
  // inclusive (gte/lte) and exclusive (gt/lt) bounds in range messages.
  const value = constraints[failedKey];
  const translated = t(`validation.${failedKey}`, { value });
  if (translated !== `validation.${failedKey}`) return translated;

  // Fallback to backend message if translation lookup returned the raw key
  return constraints.message ?? t("validation.generic.invalid");
}

/**
 * Validates a numeric value against dynamic constraints.
 * Returns the failed constraint key, or null if all pass.
 */
function checkConstraints({
  value,
  constraints,
}: {
  value: number;
  constraints: FieldConstraints;
}): "gt" | "gte" | "lt" | "lte" | null {
  if (constraints.gt != null && !(value > constraints.gt)) return "gt";
  if (constraints.gte != null && !(value >= constraints.gte)) return "gte";
  if (constraints.lt != null && !(value < constraints.lt)) return "lt";
  if (constraints.lte != null && !(value <= constraints.lte)) return "lte";
  return null;
}

/**
 * Hook to get validation props and validation function for dynamic form field types.
 * Supports both static rules from InputConfiguration and dynamic constraints
 * from backend json_schema_extra.validation.
 *
 * @see InputConfiguration for static validation options.
 */
export function useDynamicInputValidation(
  field: FormInputType,
  options: DynamicInputValidationOptions = {},
): DynamicInputValidationState {
  const { t } = useTranslation();
  const { constraints } = options;

  const defaultValidation = (
    customValidation: DynamicInputCustomValidationCallback,
  ): DynamicInputValidationState["validate"] => {
    return (value: DataFieldValue): DynamicInputValidationResult => {
      // Required check
      if (
        options.required &&
        (value === "" || value === null || value === undefined)
      ) {
        return {
          value,
          success: false,
          errors: [t("validation.required")],
        };
      }

      // Empty optional fields pass
      if (!options.required && isEmpty(value)) {
        return { value, success: true };
      }

      // Stage 1: Static validate (format/pattern checks)
      const isValid = customValidation(value);

      if (!isValid) {
        return {
          value,
          success: false,
          errors: InputConfiguration[field]?.validation?.format
            ? [
                t("validation.generic.invalidFormat", {
                  format: InputConfiguration[field].validation.format,
                }),
              ]
            : [t("validation.generic.invalid")],
        };
      }

      // Stage 2: Dynamic constraint checks
      if (constraints) {
        const normalized = value?.toString()?.replace(",", ".");
        const num = Number(normalized);

        if (Number.isNaN(num)) {
          // Non-numeric value on a constrained field — reject it
          return {
            value,
            success: false,
            errors: [t("validation.generic.invalid")],
          };
        }

        const failedKey = checkConstraints({ value: num, constraints });

        if (failedKey) {
          return {
            value,
            success: false,
            errors: [buildConstraintError({ t, constraints, failedKey })],
          };
        }
      }

      return { value, success: true };
    };
  };

  const validationProps = useMemo<
    DynamicInputValidationState["validationProps"]
  >(() => {
    const props: DynamicInputValidationState["validationProps"] = {};

    const { maxLength, min, max, onKeyDown } =
      InputConfiguration[field]?.validation || {};

    if (options.required) {
      props.required = true;
    }
    if (!isEmpty(maxLength)) {
      props.maxLength = maxLength;
    }

    // Dynamic constraints override static min/max
    const effectiveMin = constraints?.gte ?? constraints?.gt ?? min;
    const effectiveMax = constraints?.lte ?? constraints?.lt ?? max;

    if (!isEmpty(effectiveMin)) {
      props.min = effectiveMin;
    }
    if (!isEmpty(effectiveMax)) {
      props.max = effectiveMax;
    }

    if (onKeyDown) {
      props.onKeyDown = onKeyDown;
    }

    return props;
  }, [field, constraints, options.required]);

  return {
    validationProps,
    validate: defaultValidation(
      InputConfiguration[field]?.validation?.validate
        ? InputConfiguration[field]?.validation?.validate
        : () => true,
    ),
  };
}
